import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { useNotifications } from './NotificationSystem';
import { handleApiError } from '../utils/errorHandler';
import ConfirmModal from './ConfirmModal';
import Loader from './Loader';
import './Settings.css';

const Settings: React.FC = () => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotifications();

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      // deleteAccount() already calls forceLogout() internally which:
      // - Cancels all pending requests
      // - Clears all tokens (localStorage + sessionStorage)
      // - Triggers global 401 handler to redirect
      await apiService.deleteAccount();
      
      // Show success message (redirect will happen via global 401 handler)
      showSuccess('Account Deleted', 'Your account has been deleted successfully.');
      
      // Global 401 handler will redirect - no timeout needed
    } catch (error: any) {
      // If 401, account was deleted but we got unauthorized - still redirect
      if (error.status === 401 || error.errorCode === 'UNAUTHORIZED') {
        // Account was deleted, force logout and redirect
        apiService.forceLogout();
        showSuccess('Account Deleted', 'Your account has been deleted successfully.');
        navigate('/login', { replace: true });
        return;
      }
      
      const appError = handleApiError(error);
      showError('Deletion Failed', appError.message);
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  if (isDeleting) {
    return <Loader message="Deleting your account..." size="large" />;
  }

  return (
    <div className="settings">
      <div className="settings-header">
        <h1>Settings</h1>
        <p>Manage your account settings</p>
      </div>

      <div className="settings-content">
        <div className="settings-section">
          <h2>Account</h2>
          <div className="settings-item">
            <div className="settings-item-info">
              <h3>Delete Account</h3>
              <p>Permanently delete your account and all associated data. This action cannot be undone.</p>
            </div>
            <button
              className="delete-account-btn"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isDeleting}
            >
              Delete Account
            </button>
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteAccount}
        title="Delete Account"
        message="This will permanently delete your account and all associated data. This action cannot be undone."
        confirmLabel="Delete Account"
        cancelLabel="Cancel"
        confirmVariant="danger"
        loading={isDeleting}
      />
    </div>
  );
};

export default Settings;

